import produce from 'immer';
import reactUpdate, {Spec} from 'immutability-helper';
import {UpdateSpecPath} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import {FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {
  createDeleteTokenUpdater,
  createInsertTokenUpdater,
  createRenameTableTokenUpdater,
  createReorderTokenUpdater,
  createSortRowsTokenUpdater,
  TokenUpdater,
} from 'notebook/tables/excess/ops/TokenUpdater';
import tokenizer, {Token} from 'notebook/tables/excess/Tokenizer';
import * as util from 'notebook/tables/excess/Util';
import {
  CELL_FORMAT_PROPERTIES,
  COLUMN_FIELD_DEFAULTS,
  Cell,
  CellId,
  Cells,
  InsertableColumnInfo,
  MultiSelectionBox,
  RowFields,
  TABLE_COLUMN,
  TABLE_ROW,
  Table,
  TableDimension,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {omit} from 'util/omitKey';
import pick from 'util/pick';
import reorderSubsequence from 'util/reorderSubsequence';
import {ArrayElement} from 'util/Types';

export interface TableOpOutput {
  tables: FullTableMap;
  box: MultiSelectionBox | null;
}

/**
 * Shifts cell text by the given token updater if the cell text is a formula.
 *
 * @param text           The text of the cell, whether it has a formula or not
 * @param tokenUpdater   Token updater function
 * @param cellPosition   The position of the cell containing the token
 * @returns              New text of the cell, or null if the cell should not be updated
 */
function shiftCellText(text: string, tokenUpdater: TokenUpdater, cellPosition: CellId): string | null {
  // Skip cells that are not formulas
  const formulaText = util.getFormulaText(text);
  if (formulaText == null) {
    return null;
  }

  let tokens: Array<Token>;
  try {
    tokens = tokenizer.tokenize(formulaText);
  } catch (e) {
    // Skip cells that have an invalid formula
    if (e instanceof util.FormulaTokenizeError) {
      return null;
    }
    throw e;
  }

  // Reconstruct the formula with the token updates
  const updatedFormulaText = tokens.map((token) => tokenUpdater(token, cellPosition) ?? token.text).join('');
  return updatedFormulaText === formulaText ? null : `=${updatedFormulaText}`;
}

/**
 * Shift references (over all cells in `tables`) using the provided token updater.
 *
 * @param tables        Map from table name to table. Treated as immutable.
 * @param tokenUpdater  Token updater function
 * @param dim           One of [TABLE_ROW, TABLE_COLUMN]
 * @return              New map from table name to table
 */
function shiftFormulas(tables: FullTableMap, tokenUpdater: TokenUpdater, dim?: TableDimension): FullTableMap {
  return _.mapObject(tables, (currentTable, currentTableName) => {
    const tableUpdates: UpdateSpecPath = {};
    const cellUpdates: {[index: number]: {[index: number]: UpdateSpecPath}} = {};
    for (let i = 0; i < currentTable.table.length; i++) {
      const row = currentTable.table[i];
      for (let j = 0; j < row.length; j++) {
        // If there are updates to the formula, register the change as a table update
        const cell = row[j];
        const newText = shiftCellText(cell.text, tokenUpdater, {row: i, column: j, table: currentTableName});
        if (newText != null) {
          // and then register it as a change to the table
          cellUpdates[i] ??= {};
          cellUpdates[i][j] = {text: {$set: newText}};
        }
      }
    }
    if (!_.isEmpty(cellUpdates)) {
      tableUpdates.table = cellUpdates;
    }

    // For DataTables, we also need to shift the associated config columns' defaultValues as these contain
    // row-restricted formulas.
    // TODO: We need to also do this in some cases where dim != column, like when renaming tables.
    if (dim === TABLE_COLUMN) {
      for (let j = 0; j < currentTable.columnConstraints.length; j++) {
        const columnConstraint = currentTable.columnConstraints[j];
        if (columnConstraint?.structuredTableColumn?.defaultValue != null) {
          const newText = shiftCellText(
            columnConstraint.structuredTableColumn.defaultValue.text,
            tokenUpdater,
            {row: 0, column: j, table: currentTableName} // row=0 because a default value assumes it's in the top row
          );
          if (newText != null) {
            tableUpdates.columnConstraints ??= {};
            // @ts-expect-error
            tableUpdates.columnConstraints[j] = {
              structuredTableColumn: {defaultValue: {text: {$set: newText}}},
            };
          }
        }
      }
    }

    // If there are updates registered for the table, apply them; otherwise, return the unchanged table.
    if (!_.isEmpty(tableUpdates)) {
      return reactUpdate(currentTable, tableUpdates);
    } else {
      return currentTable;
    }
  });
}

/**
 * Shift all fields in `table` that depend on the number of rows.
 */
function shiftRowFields(table: Table, op: 'insert' | 'delete', numRows: number, atIndex: number): Table {
  const numColumns = table.table[0].length;
  // If someone adds another RowField, this type will remind them to add a default value here.
  const rowFieldDefaults: Required<{
    [fieldName in keyof RowFields]: ArrayElement<RowFields[fieldName]>;
  }> = {
    table: new Array(numColumns).fill({text: ''}),
    rowData: {},
    rowLabels: '',
  };

  const fieldNamesPresent = Object.keys(rowFieldDefaults).filter(
    (fieldName) => table[fieldName as keyof RowFields] != null
  ) as Array<keyof RowFields>;

  const updates: UpdateSpecPath = {};
  for (const fieldName of fieldNamesPresent) {
    if (op === 'insert') {
      updates[fieldName] = {
        $splice: [[atIndex, 0, ...Array(numRows).fill(rowFieldDefaults[fieldName])]],
      };
    } else if (op === 'delete') {
      updates[fieldName] = {$splice: [[atIndex, numRows]]};
    }
  }

  return reactUpdate(table, updates);
}

/**
 * Reorder all fields in `table` that depend on row or column order.
 */
function reorderFields(table: Table, newToOldIndices: Array<number>, dim: TableDimension): Table {
  let fieldNames: Array<keyof RowFields | keyof typeof COLUMN_FIELD_DEFAULTS> = [];
  if (dim === TableDimension.ROW) {
    fieldNames = Object.keys({
      table: null,
      rowData: null,
      rowLabels: null,
      // This is an ugly Typescript hack, but this ensures that if someone adds another RowField,
      // this type will remind them to include it here.
    } satisfies Required<{
      [fieldName in keyof RowFields]: unknown;
    }>) as Array<keyof RowFields>;
  } else {
    fieldNames = Object.keys(COLUMN_FIELD_DEFAULTS) as Array<keyof typeof COLUMN_FIELD_DEFAULTS>;
  }

  const updates: UpdateSpecPath = {};
  for (const fieldName of fieldNames) {
    const field = table[fieldName];
    if (field != null) {
      updates[fieldName] = {
        $set: newToOldIndices.map((newIndex) => field[newIndex]),
      };
    }
  }

  if (dim === TableDimension.COLUMN) {
    // Also reorder the cells in each row
    const newTable = table.table.map((row) => newToOldIndices.map((newIndex) => row[newIndex]));
    updates.table = {
      $set: newTable,
    };
  }

  return reactUpdate(table, updates);
}

/**
 * Same as deleteRows but allows for non-contiguous row deletion to be performed in a single operation.
 *
 * @param tables
 * @param targetTableName
 * @param ranges - Array of [start, end] pairs (both inclusive) representing the rows to be deleted
 */
export function deleteRowsNonContiguous(
  tables: FullTableMap,
  targetTableName: string,
  ranges: Array<[number, number]>
): TableOpOutput {
  assert(Object.keys(tables).includes(targetTableName), `unknown table: ${targetTableName}`);
  const numRows = tables[targetTableName].table.length;

  // sort the ranges in desc order, so we don't have to worry about shifting indices
  ranges = ranges.sort(([start1], [start2]) => start2 - start1);
  const minIdxInRanges = ranges[ranges.length - 1][0];
  const maxIdxInRanges = ranges[0][1];
  assert(
    0 <= minIdxInRanges && minIdxInRanges <= maxIdxInRanges && maxIdxInRanges <= numRows - 1,
    `cannot delete rows outside of table. [${minIdxInRanges}, ${maxIdxInRanges}] outside of [0, ${
      numRows - 1
    }]`
  );

  let [minI, maxI] = ranges[0];
  let newTables = tables;

  for (const [start, end] of ranges) {
    minI = Math.min(minI, start);
    maxI = Math.max(maxI, end);
    ({tables: newTables} = deleteRows(newTables, targetTableName, start, end));
  }

  return {
    tables: newTables,
    box: {
      minI: Math.min(minI, newTables[targetTableName].table.length - 1),
      maxI: Math.min(maxI, newTables[targetTableName].table.length - 1),
      minJ: 0,
      maxJ: tables[targetTableName].table[0].length - 1,
    },
  };
}

/**
 * @param tables           Map from table name to table
 * @param targetTableName  The table from which cells will be deleted
 * @param minI             The index of the first row to be deleted
 * @param maxI             The index of the last row to be deleted
 * @return                 The result of the deletion, consisting of:
 *                           tables: new tables
 *                           box: the region that was previously selected. If part of the previously selected
 *                                region is no longer available, the selection will shrink appropriately.
 */
export function deleteRows(
  tables: FullTableMap,
  targetTableName: string,
  minI: number,
  maxI: number
): TableOpOutput {
  assert(Object.keys(tables).includes(targetTableName), `unknown table: ${targetTableName}`);
  const numRows = tables[targetTableName].table.length;
  assert(
    0 <= minI && minI <= maxI && maxI <= numRows - 1,
    `cannot delete rows outside of table. [${minI}, ${maxI}] outside of [0, ${numRows - 1}]`
  );
  assert(
    tables[targetTableName].allowNoRows || !(minI === 0 && maxI === numRows - 1),
    `cannot delete all rows [${minI}, ${maxI}]`
  );

  const tokenUpdater = createDeleteTokenUpdater(targetTableName, TABLE_ROW, minI, maxI);
  const newTables = shiftFormulas(tables, tokenUpdater, TABLE_ROW);
  newTables[targetTableName] = shiftRowFields(newTables[targetTableName], 'delete', maxI - minI + 1, minI);

  return {
    tables: newTables,
    box: {
      minI: Math.min(minI, newTables[targetTableName].table.length - 1),
      maxI: Math.min(maxI, newTables[targetTableName].table.length - 1),
      minJ: 0,
      maxJ: tables[targetTableName].table[0].length - 1,
    },
  };
}

/**
 * @param tables           Map from table name to table
 * @param targetTableName  The table from which cells will be deleted
 * @param minJ             The index of the first column to be deleted
 * @param maxJ             The index of the last column to be deleted
 * @return                 The result of the deletion, consisting of:
 *                           tables: new tables
 *                           box: the region that was previously selected. If part of the previously selected
 *                                region is no longer available, the selection will shrink appropriately.
 */
export function deleteColumns(
  tables: FullTableMap,
  targetTableName: string,
  minJ: number,
  maxJ: number
): TableOpOutput {
  assert(Object.keys(tables).includes(targetTableName), `unknown table: ${targetTableName}`);
  const oldTable = tables[targetTableName];
  const numColumns = oldTable.table[0].length;
  assert(
    0 <= minJ && minJ <= maxJ && maxJ <= numColumns - 1,
    `cannot delete columns outside of table [${minJ}, ${maxJ}] outside of [0, ${numColumns - 1}]`
  );
  assert(!(minJ === 0 && maxJ === numColumns - 1), `cannot delete all columns [${minJ}, ${maxJ}]`);

  const tokenUpdater = createDeleteTokenUpdater(targetTableName, TableDimension.COLUMN, minJ, maxJ);
  const newTables = shiftFormulas(tables, tokenUpdater, TABLE_COLUMN);

  // Delete columns from the target table
  const spliceOp: Spec<Array<Cell>> = {$splice: [[minJ, maxJ - minJ + 1]]};
  const newTable: Table = reactUpdate(
    newTables[targetTableName],
    // @ts-expect-error
    _.chain(COLUMN_FIELD_DEFAULTS)
      .mapObject(() => spliceOp)
      .extend({
        table: {
          $apply(table: Array<Array<Cell>>): Array<Array<Cell>> {
            return table.map((row) => reactUpdate(row, spliceOp));
          },
        },
      })
      .value()
  );

  newTables[targetTableName] = newTable;

  return {
    tables: newTables,
    box: {
      minI: 0,
      maxI: newTables[targetTableName].table.length - 1,
      minJ: Math.min(minJ, newTables[targetTableName].table[0].length - 1),
      maxJ: Math.min(maxJ, newTables[targetTableName].table[0].length - 1),
    },
  };
}

/**
 * @param tables           Map from table name to table
 * @param targetTableName  The table into which cells will be inserted
 * @param i                The index of the row above which the rows will be inserted.
 * @param numNewRows       The number of new rows that will be inserted.
 * @return                 New table map
 */
export function insertRowsHelper(
  tables: FullTableMap,
  targetTableName: string,
  i: number,
  numNewRows: number
): FullTableMap {
  assert(Object.keys(tables).includes(targetTableName), `unknown table: ${targetTableName}`);
  const numRows = tables[targetTableName].table.length;
  assert(0 <= i && i <= numRows, `unknown row index ${i}, outside of possible range [0, ${numRows}]`);
  assert(numNewRows >= 1, `expected a positive number of new rows. specified number was ${{numNewRows}}`);

  const tokenUpdater = createInsertTokenUpdater(targetTableName, TABLE_ROW, i, numNewRows);
  const newTables = shiftFormulas(tables, tokenUpdater, TABLE_ROW);
  newTables[targetTableName] = shiftRowFields(newTables[targetTableName], 'insert', numNewRows, i);
  return newTables;
}

/**
 * @param tables           Map from table name to table
 * @param targetTableName  The table into which cells will be inserted
 * @param minI             The index of the first row selected
 * @param maxI             The index of the last row selected
 * @return                 The result of the insertion, consisting of:
 *                           tables: new tables
 *                           box: the newly inserted region
 */
export function insertRowsAbove(
  tables: FullTableMap,
  targetTableName: string,
  minI: number,
  maxI: number
): TableOpOutput {
  const numRows = tables[targetTableName].table.length;
  assert(
    0 <= minI && minI <= maxI && maxI <= numRows - 1,
    `rows selected were [${minI}, ${maxI}] outside of [0, ${numRows - 1}]`
  );

  const numColumns = tables[targetTableName].table[0].length;
  const numNewRows = maxI - minI + 1;
  return {
    tables: insertRowsHelper(tables, targetTableName, minI, numNewRows),
    box: {
      minI,
      maxI,
      minJ: 0,
      maxJ: numColumns - 1,
    },
  };
}

/**
 * @param tables           Map from table name to table
 * @param targetTableName  The table into which cells will be inserted
 * @param minI             The index of the first row selected
 * @param maxI             The index of the last row selected
 * @return                 The result of the insertion, consisting of:
 *                           tables: new tables
 *                           box: the newly inserted region
 */
export function insertRowsBelow(
  tables: FullTableMap,
  targetTableName: string,
  minI: number,
  maxI: number
): TableOpOutput {
  const numRows = tables[targetTableName].table.length;
  assert(
    0 <= minI && minI <= maxI && maxI <= numRows - 1,
    `rows selected were [${minI}, ${maxI}] outside of [0, ${numRows - 1}]`
  );

  const numColumns = tables[targetTableName].table[0].length;
  const numNewRows = maxI - minI + 1;
  return {
    tables: insertRowsHelper(tables, targetTableName, maxI + 1, numNewRows),
    box: {
      minI: maxI + 1,
      maxI: maxI + numNewRows,
      minJ: 0,
      maxJ: numColumns - 1,
    },
  };
}

/**
 * @package Private helper
 */
export function insertColumnsHelper(
  tables: FullTableMap,
  targetTableName: string,
  j: number,
  numNewColumns: number,
  columnFields?: Partial<InsertableColumnInfo>,
  initialCellValues?: Array<InitialCellValue>
): FullTableMap {
  if (columnFields == null) {
    columnFields = {};
  }
  assert(Object.keys(tables).includes(targetTableName), `unknown table: ${targetTableName}`);
  const numColumns = tables[targetTableName].table[0].length;
  assert(
    0 <= j && j <= numColumns,
    `unknown column index ${j}, outside of possible range [0, ${numColumns}]`
  );
  assert(
    numNewColumns >= 1,
    `expected a positive number of new columns. specified number was ${{numNewColumns}}`
  );
  if (initialCellValues) {
    const numRows = tables[targetTableName].table.length;
    assert(initialCellValues.length === numRows, 'Expected one initial cell value per row');
  }

  const tokenUpdater = createInsertTokenUpdater(targetTableName, TABLE_COLUMN, j, numNewColumns);
  const newTables = shiftFormulas(tables, tokenUpdater, TABLE_COLUMN);

  // Insert columns in target table
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-function-return-type
  const spliceOp = (value: any) => ({$splice: [[j, 0, value]]});

  // eslint-disable-next-line no-restricted-syntax
  _.times(numNewColumns, () => {
    newTables[targetTableName] = reactUpdate(
      newTables[targetTableName],
      // @ts-expect-error
      _.chain(COLUMN_FIELD_DEFAULTS)
        .mapObject((defaultValue, field) =>
          // @ts-expect-error
          spliceOp(columnFields[field] != null ? columnFields[field] : defaultValue)
        )
        .extend({
          table: {
            $apply(table: Array<Array<Cell>>): Array<Array<Cell>> {
              return table.map((row: Array<Cell>, i: number) =>
                // TS gets confused by the reactUpdate types, so just use any for now.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                reactUpdate(row, spliceOp(initialCellValues?.[i] ?? {text: ''}) as any)
              );
            },
          },
        })
        .value()
    );
  });

  return newTables;
}

/**
 * Inserts columns to the left of the selected columns.
 *
 * @param tables             Map from table name to table
 * @param targetTableName    The table into which cells will be inserted
 * @param minJ               The index of the first column selected
 * @param maxJ               The index of the last column selected
 * @param columnInfo         The column info for the new columns
 * @param initialCellValues  The initial values for cells in the new columns
 * @returns                  The result of the insertion, consisting of:
 *                             tables: new tables
 *                             box: the newly inserted region
 */
export function insertColumnsLeft(
  tables: FullTableMap,
  targetTableName: string,
  minJ: number,
  maxJ: number,
  columnInfo: Partial<InsertableColumnInfo> = {},
  initialCellValues?: Array<InitialCellValue>
): TableOpOutput {
  const numColumns = tables[targetTableName].table[0].length;
  assert(
    0 <= minJ && minJ <= maxJ && maxJ <= numColumns - 1,
    `columns selected were [${minJ}, ${maxJ}] outside of [0, ${numColumns - 1}]`
  );

  const numRows = tables[targetTableName].table.length;
  const numNewColumns = maxJ - minJ + 1;
  // set the width to the specified width or the width of the column to the right
  const newColumnWidth = columnInfo.widths ?? tables[targetTableName].widths[minJ];
  return {
    tables: insertColumnsHelper(
      tables,
      targetTableName,
      minJ,
      numNewColumns,
      {
        ...columnInfo,
        widths: newColumnWidth,
      },
      initialCellValues
    ),
    box: {
      minI: 0,
      maxI: numRows - 1,
      minJ,
      maxJ,
    },
  };
}

/**
 * Inserts columns to the right of the selected columns.
 *
 * @param tables             Map from table name to table
 * @param targetTableName    The table into which cells will be inserted
 * @param minJ               The index of the first column selected
 * @param maxJ               The index of the last column selected
 * @param columnInfo         The column info for the new columns
 * @param initialCellValues  The initial values for cells in the new columns
 * @returns                  The result of the insertion, consisting of:
 *                             tables: new tables
 *                             box: the newly inserted region
 */
export function insertColumnsRight(
  tables: FullTableMap,
  targetTableName: string,
  minJ: number,
  maxJ: number,
  columnInfo: Partial<InsertableColumnInfo> = {},
  initialCellValues?: Array<InitialCellValue>
): TableOpOutput {
  const numColumns = tables[targetTableName].table[0].length;
  assert(
    0 <= minJ && minJ <= maxJ && maxJ <= numColumns - 1,
    `columns selected were [${minJ}, ${maxJ}] outside of [0, ${numColumns - 1}]`
  );

  const numRows = tables[targetTableName].table.length;
  const numNewColumns = maxJ - minJ + 1;
  // set the width to the specified width or the width of the column to the left
  const newColumnWidth = columnInfo.widths ?? tables[targetTableName].widths[maxJ];

  return {
    tables: insertColumnsHelper(
      tables,
      targetTableName,
      maxJ + 1,
      numNewColumns,
      {
        ...columnInfo,
        widths: newColumnWidth,
      },
      initialCellValues
    ),
    box: {
      minI: 0,
      maxI: numRows - 1,
      minJ: maxJ + 1,
      maxJ: maxJ + numNewColumns,
    },
  };
}

interface ReorderSortOptions {
  /**
   * Mapping from old row index to new row index.
   */
  newToOldIndices: Array<number>;
}

export interface ReorderManualOptions {
  /**
   * First index of sequence of elements to move, inclusive.
   */
  startIndex: number;
  /**
   * Last index of sequence of elements to move, inclusive.
   */
  endIndex: number;
  /**
   * New index of first element of sequence in original order index-space.
   */
  insertPosition: number;
  /**
   * The dimension (row or column) along which to reorder.
   */
  dim: TableDimension;
}

export type ReorderOptions = ReorderSortOptions | ReorderManualOptions;

function isReorderOptionsForSort(options: ReorderOptions): options is ReorderSortOptions {
  return options.hasOwnProperty('newToOldIndices');
}

/**
 * Reorders rows or columns in a table and updates table formulas to reflect reordering.
 * Updates cell references differently depending on whether the reordering is a sort or a manual reordering.
 *
 * @param  tables           Map from table name to table
 * @param  targetTableName  The table in which rows should be reordered
 * @param  options          Options describing how to reorder the rows or columns
 * @return                  The result of the reordering, consisting of:
 *                            tables: new tables
 *                            box: null
 */
export function reorderRowsOrColumns(
  tables: FullTableMap,
  targetTableName: string,
  options: ReorderOptions
): TableOpOutput {
  assert(Object.keys(tables).includes(targetTableName), `unknown table: ${targetTableName}`);

  const dim = isReorderOptionsForSort(options) ? TableDimension.ROW : options.dim;
  const dimensionSize =
    dim === TableDimension.ROW
      ? tables[targetTableName].table.length
      : tables[targetTableName].table[0].length;

  let tokenUpdater: TokenUpdater;
  let newToOldIndices: Array<number>;

  if (isReorderOptionsForSort(options)) {
    // Sorting, so validate the newToOldIndices array
    newToOldIndices = options.newToOldIndices;
    assert(
      newToOldIndices.length === dimensionSize,
      'Expected newToOldIndices to have the same length as the number of rows'
    );
    assert(
      new Set(Object.values(newToOldIndices)).size === dimensionSize,
      'Cannot move multiple rows to the same row position'
    );
    assert(
      Object.values(newToOldIndices).every((newIndex) => 0 <= newIndex && newIndex < dimensionSize),
      `Expected every index to be in range [0, ${dimensionSize - 1}]`
    );

    tokenUpdater = createSortRowsTokenUpdater(targetTableName, dim, newToOldIndices);
  } else {
    // Manual reordering, so validate the arguments
    const {startIndex, endIndex, insertPosition} = options;
    assert(
      0 <= startIndex && startIndex <= endIndex && endIndex < dimensionSize,
      'Expected startIndex and endIndex to be in range'
    );
    assert(0 <= insertPosition && insertPosition <= dimensionSize, 'Expected insertIndex to be in range');

    newToOldIndices = reorderSubsequence(
      Array.from(Array(dimensionSize), (_, i) => i), // Array of indices [0, ..., dimensionSize - 1]
      {
        insertPosition,
        startIndex,
        endIndex: endIndex + 1, // + 1 because endIndex is inclusive but argument should be exclusive
      }
    );

    tokenUpdater = createReorderTokenUpdater(targetTableName, dim, startIndex, endIndex, newToOldIndices);
  }

  const newTables = shiftFormulas(tables, tokenUpdater, dim);
  newTables[targetTableName] = reorderFields(newTables[targetTableName], newToOldIndices, dim);

  return {
    tables: newTables,
    box: null, // Don't change the selection
  };
}

export function mergeIntoSingleInput(
  tables: FullTableMap,
  computedTables: OutputTableMap,
  targetTableName: string,
  startIndex: number,
  endIndex: number
): TableOpOutput {
  return mergeOrUnmergeSingleInput(tables, computedTables, targetTableName, startIndex, endIndex, true);
}

export function unmergeSingleInput(
  tables: FullTableMap,
  computedTables: OutputTableMap,
  targetTableName: string,
  startIndex: number,
  endIndex: number
): TableOpOutput {
  return mergeOrUnmergeSingleInput(tables, computedTables, targetTableName, startIndex, endIndex, false);
}

function mergeOrUnmergeSingleInput(
  tables: FullTableMap,
  computedTables: OutputTableMap,
  targetTableName: string,
  startIndex: number,
  endIndex: number,
  toSingleInput: boolean
): TableOpOutput {
  const targetTable = tables[targetTableName];
  const numRows = targetTable.table.length;
  const singleInputBoundaryIndex = TableUtil.findSingleInputBoundaryIndex(targetTable);

  // merge or unmerge each column
  let tablesWithMergeChanges: FullTableMap = tables;
  for (let columnIndex = startIndex; columnIndex <= endIndex; columnIndex++) {
    const selection: MultiSelectionBox = {
      minI: 0,
      maxI: numRows - 1,
      minJ: columnIndex,
      maxJ: columnIndex,
    };
    tablesWithMergeChanges = (
      toSingleInput
        ? mergeRegion(tablesWithMergeChanges, computedTables, targetTableName, selection)
        : unmergeRegion(tablesWithMergeChanges, computedTables, targetTableName, selection)
    ).tables;
  }

  // set isSingleInput accordingly on each column
  // (we do this in a separate loop to reduce immer updates)
  tablesWithMergeChanges = produce(tablesWithMergeChanges, (draft) => {
    for (let columnIndex = startIndex; columnIndex <= endIndex; columnIndex++) {
      const columnConstraint = draft[targetTableName].columnConstraints[columnIndex];
      // Single Inputs are a Structured Table-only feature
      if (columnConstraint?.structuredTableColumn) {
        columnConstraint.structuredTableColumn.isSingleInput = toSingleInput;
      }
    }
  });

  return moveBetweenSingleInputsAndColumns(
    tablesWithMergeChanges,
    targetTableName,
    singleInputBoundaryIndex,
    startIndex,
    endIndex
  );
}

function moveBetweenSingleInputsAndColumns(
  tables: FullTableMap,
  targetTableName: string,
  singleInputBoundaryIndex: number,
  startIndex: number,
  endIndex: number
): TableOpOutput {
  return reorderRowsOrColumns(tables, targetTableName, {
    startIndex,
    endIndex,
    insertPosition: singleInputBoundaryIndex,
    dim: TableDimension.COLUMN,
  });
}

/**
 * Renames the table with current name `targetTableName` to `newTableName`.
 *
 * @param tables           Map from table name to table
 * @param targetTableName  The name of the table to rename.
 * @param newTableName     The new name for the table.
 * @return                 The result of the rename, consisting of:
 *                           tables: new tables
 *                           box: null
 */
export function renameTable(
  tables: FullTableMap,
  targetTableName: string,
  newTableName: string
): TableOpOutput {
  const existingNames = Object.keys(tables);
  assert(existingNames.includes(targetTableName), `No table with name ${targetTableName}`);
  assert(!existingNames.includes(newTableName), `Existing table with name ${newTableName}`);

  const tokenUpdater = createRenameTableTokenUpdater(targetTableName, newTableName);
  const newTables = shiftFormulas(tables, tokenUpdater);

  newTables[targetTableName] = reactUpdate(newTables[targetTableName], {name: {$set: newTableName}});

  return {tables: newTables, box: null};
}

export function _makeCell(text: string): Cell {
  return {text};
}

/**
 * Merges region defined by `selection` in the table `targetTableName`.
 *
 * @param tables           Map from table name to table
 * @param computedTables   Map from table name to evaluated table
 * @param targetTableName  The table in which cells will be merged.
 * @param selection        Bounds of region to be merged.
 * @return                 The result of the merge, consisting of:
 *                           tables: new tables
 *                           box: the newly merged region
 */
export function mergeRegion(
  tables: FullTableMap,
  computedTables: OutputTableMap,
  targetTableName: string,
  selection: MultiSelectionBox
): TableOpOutput {
  const {minI, minJ} = selection;
  const newTableCells = TableUtil.mergeTableRegion(tables[targetTableName], selection).table;

  const newTables = _.mapObject(tables, (table, name) => {
    if (name === targetTableName) {
      const newStepTable = reactUpdate(table, {table: {$set: newTableCells}});
      return newStepTable;
    } else {
      return table;
    }
  });

  return {
    tables: newTables,
    box: {
      minI,
      maxI: minI,
      minJ,
      maxJ: minJ,
    },
  };
}

/**
 * Unmerges all cells in the region defined by `selection` in the table `targetTableName`.
 *
 * @param tables           Map from table name to table
 * @param computedTables   Map from table name to evaluated table
 * @param targetTableName  The table in which cells will be unmerged.
 * @param selection        Bounds of region to unmerge all cells inside.
 * @return                 The result of the merge, consisting of:
 *                           tables: new tables
 *                           box: selection
 */
export function unmergeRegion(
  tables: FullTableMap,
  computedTables: OutputTableMap,
  targetTableName: string,
  selection: MultiSelectionBox
): TableOpOutput {
  const {minI, maxI, minJ, maxJ} = selection;
  const tableUpdates: Spec<Cells> = {};
  for (let i = minI, end = maxI + 1, asc = minI <= end; asc ? i < end : i > end; asc ? i++ : i--) {
    for (let j = minJ, end1 = maxJ + 1, asc1 = minJ <= end1; asc1 ? j < end1 : j > end1; asc1 ? j++ : j--) {
      if (TableUtil.isRenderedMergedCell(i, j, computedTables[targetTableName][i][j])) {
        tableUpdates[i] ??= {};
        // @ts-expect-error
        tableUpdates[i][j] = {
          $apply(cell: Cell): Cell {
            return omit(cell, 'mergeRowSpan', 'mergeColSpan');
          },
        };

        const sourceCell: Cell = tables[targetTableName].table[i][j];
        const mergeCell = {
          ...omit(
            pick(sourceCell, ...CELL_FORMAT_PROPERTIES, 'text', 'typedValue'),
            'mergeRowSpan',
            'mergeColSpan'
          ),
        };
        for (let s = i; s < i + sourceCell.mergeRowSpan!; s++) {
          for (let t = j; t < j + sourceCell.mergeColSpan!; t++) {
            if (s === i && t === j) {
              continue;
            }
            tableUpdates[s] ??= {};
            // @ts-expect-error
            tableUpdates[s][t] = {
              $apply(cell: Cell): Cell {
                return {
                  ...cell,
                  ...mergeCell,
                };
              },
            };
          }
        }
      }
    }
  }

  const newTables = _.mapObject(tables, (table, name) => {
    if (name === targetTableName) {
      const newStepTable = reactUpdate(table, {table: tableUpdates});
      return newStepTable;
    } else {
      return table;
    }
  });

  return {
    tables: newTables,
    box: {minI, maxI, minJ, maxJ},
  };
}
